import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, ThumbsUp, ThumbsDown, BookOpen, GraduationCap, Download, Megaphone, ChevronRight, Loader2, MessageSquarePlus, Trash2, LifeBuoy, X, CheckCircle2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Badge } from "@/components/ui-em";

const SOURCE_ICONS = {
  article: BookOpen,
  training: GraduationCap,
  download: Download,
  announcement: Megaphone,
};

function ConfidenceBadge() {
  // Confidence badges were removed from the UI to avoid demotivating leaders.
  return null;
}

function SourceRow({ source }) {
  const { t } = useTranslation();
  const Icon = SOURCE_ICONS[source.source_type] || BookOpen;
  const label = {
    article: t("ai.sourceKb"),
    training: t("ai.sourceAcademy"),
    download: t("ai.sourceDownloads"),
    announcement: t("ai.sourceAnnouncement"),
  }[source.source_type] || source.source_type;
  const linkable = source.source_type === "article"
    ? `/knowledge-base/${source.source_id}`
    : source.source_type === "training"
    ? "/academy"
    : source.source_type === "announcement"
    ? "/announcements"
    : "/downloads";
  return (
    <Link
      to={linkable}
      className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:border-gold/30 hover:bg-gold/[0.04] group"
      style={{ transitionProperty: "background-color, border-color", transitionDuration: "180ms" }}
    >
      <div className="w-8 h-8 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-gold" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
        <div className="text-sm text-white truncate group-hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>{source.source_title}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-gold group-hover:translate-x-0.5" style={{ transitionProperty: "color, transform", transitionDuration: "160ms" }} />
    </Link>
  );
}

function Feedback({ messageId, existing }) {
  const { t } = useTranslation();
  const [state, setState] = useState(existing || null); // { helpful, comment }
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const send = async (helpful, commentText) => {
    setSubmitting(true);
    try {
      await api.post("/ai/feedback", { message_id: messageId, helpful, comment: commentText || undefined });
      setState({ helpful, comment: commentText });
      if (helpful) toast.success(t("ai.thanks"));
      else toast.success(t("ai.sentToReview"));
      setShowComment(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (state) {
    return (
      <div className="text-[11px] text-zinc-500 mt-3 pt-3 border-t border-white/5">
        {state.helpful ? t("ai.markedHelpful") : t("ai.markedNotHelpful")}
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-white/5">
      {!showComment ? (
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          <span>{t("ai.wasHelpful")}</span>
          <button
            onClick={() => send(true)}
            disabled={submitting}
            data-testid={`feedback-yes-${messageId}`}
            className="p-1.5 rounded-md text-zinc-500 hover:text-emerald-300 hover:bg-emerald-500/10"
            style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
          >
            <ThumbsUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowComment(true)}
            data-testid={`feedback-no-${messageId}`}
            className="p-1.5 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
            style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
          >
            <ThumbsDown className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-zinc-500">{t("ai.commentPrompt")}</div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="em-input text-sm"
            placeholder={t("ai.commentPlaceholder")}
            autoFocus
          />
          <div className="flex gap-2">
            <button onClick={() => send(false, comment)} disabled={submitting} className="text-xs bg-gold text-ink-0 font-semibold rounded-md px-3 py-1.5 hover:bg-gold-hover" style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}>
              {t("ai.commentSubmit")}
            </button>
            <button onClick={() => setShowComment(false)} className="text-xs text-zinc-500 hover:text-white px-3 py-1.5">
              {t("ai.commentCancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AssistantMessage({ msg, conversationId, userQuestion }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-4">
      <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center shrink-0">
        <Sparkles className="w-4 h-4 text-gold" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold mb-2">EM AI</div>
        <div className="em-prose text-sm">
          <ReactMarkdown>{msg.content || ""}</ReactMarkdown>
        </div>
        {msg.sources?.length > 0 && (
          <div className="mt-6 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">{t("ai.sourcesLabel")}</div>
            {msg.sources.map((s, i) => (
              <SourceRow key={i} source={s} />
            ))}
          </div>
        )}
        <AnswerFeedback
          messageId={msg.id}
          conversationId={conversationId}
          question={userQuestion || ""}
          aiAnswer={msg.content || ""}
        />
      </div>
    </div>
  );
}

function AnswerFeedback({ messageId, conversationId, question, aiAnswer }) {
  const { t } = useTranslation();
  const [state, setState] = useState("idle"); // idle | positive | escalated
  const [showEscalation, setShowEscalation] = useState(false);

  if (!messageId) return null;

  const yes = async () => {
    setState("positive");
    try {
      await api.post("/ai/feedback", { message_id: messageId, rating: 1, comment: "" });
    } catch {
      // silent
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-white/[0.06]">
      {state === "idle" && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-zinc-500">{t("ai.escalate.prompt")}</span>
          <button
            onClick={yes}
            data-testid={`answer-yes-${messageId}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-emerald-500/10 hover:text-emerald-300 border border-white/5 hover:border-emerald-500/30"
            style={{ transitionProperty: "background-color, color, border-color", transitionDuration: "160ms" }}
          >
            <ThumbsUp className="w-3.5 h-3.5" />
            {t("ai.escalate.yes")}
          </button>
          <button
            onClick={() => setShowEscalation(true)}
            data-testid={`answer-no-${messageId}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-gold/10 hover:text-gold border border-white/5 hover:border-gold/30"
            style={{ transitionProperty: "background-color, color, border-color", transitionDuration: "160ms" }}
          >
            <LifeBuoy className="w-3.5 h-3.5" />
            {t("ai.escalate.no")}
          </button>
        </div>
      )}
      {state === "positive" && (
        <div className="inline-flex items-center gap-2 text-xs text-emerald-400">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {t("ai.escalate.thanksPositive")}
        </div>
      )}
      {state === "escalated" && (
        <div className="inline-flex items-center gap-2 text-xs text-gold">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {t("ai.escalate.thanksEscalated")}
        </div>
      )}

      {showEscalation && (
        <EscalationModal
          onClose={() => setShowEscalation(false)}
          onSubmitted={() => {
            setShowEscalation(false);
            setState("escalated");
          }}
          question={question}
          aiAnswer={aiAnswer}
          conversationId={conversationId}
          aiMessageId={messageId}
        />
      )}
    </div>
  );
}

function EscalationModal({ onClose, onSubmitted, question, aiAnswer, conversationId, aiMessageId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    additional_context: "",
    name: user?.name || "",
    email: user?.email || "",
    category: "General FAQ",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/escalations/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/escalations", {
        question,
        ai_answer: aiAnswer,
        conversation_id: conversationId,
        ai_message_id: aiMessageId,
        additional_context: form.additional_context,
        category: form.category,
        name: form.name,
        email: form.email,
      });
      toast.success(t("ai.escalate.sentToast"));
      onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-0/70 backdrop-blur-md overflow-y-auto">
      <div className="em-card p-8 w-full max-w-2xl my-8" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}>
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-gold font-semibold mb-1">{t("ai.escalate.modalEyebrow")}</p>
            <h3 className="font-display text-2xl font-light">{t("ai.escalate.modalTitle")}</h3>
            <p className="text-xs text-zinc-500 mt-2 max-w-md leading-relaxed">{t("ai.escalate.modalIntro")}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="escalate-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-5 p-4 rounded-lg bg-white/[0.03] border border-white/5">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold mb-1.5">{t("ai.escalate.yourQuestion")}</div>
          <p className="text-sm text-white leading-relaxed">{question}</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold block mb-2">{t("ai.escalate.categoryLabel")}</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="em-input"
              data-testid="escalate-category"
            >
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold block mb-2">{t("ai.escalate.contextLabel")}</label>
            <textarea
              value={form.additional_context}
              onChange={(e) => setForm({ ...form, additional_context: e.target.value })}
              rows={4}
              placeholder={t("ai.escalate.contextPlaceholder")}
              className="em-input"
              data-testid="escalate-context"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold block mb-2">{t("ai.escalate.nameLabel")}</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="em-input"
                data-testid="escalate-name"
                required
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold block mb-2">{t("ai.escalate.emailLabel")}</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="em-input"
                data-testid="escalate-email"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="escalate-submit"
            className="w-full bg-gold hover:bg-gold-hover text-ink-0 font-semibold text-sm rounded-lg px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ transitionProperty: "background-color, opacity", transitionDuration: "180ms" }}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LifeBuoy className="w-4 h-4" />}
            {t("ai.escalate.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}

function UserMessage({ msg, userName }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-4">
      <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center text-zinc-300 font-semibold text-sm shrink-0">
        {(userName || "U").charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold mb-2">{t("ai.you")}</div>
        <div className="text-sm text-white leading-relaxed whitespace-pre-wrap">{msg.content}</div>
      </div>
    </div>
  );
}

export default function AIAssistant() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const lastScrolledUserIdRef = useRef(null);

  const examplePrompts = t("ai.examplePrompts", { returnObjects: true });

  const loadConversations = () => {
    api.get("/ai/conversations", { params: { lang: i18n.language } }).then((r) => setConversations(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadConversations();
  }, [i18n.language]);

  const openConversation = async (cid) => {
    setConversationId(cid);
    setMessages([]);
    try {
      const { data } = await api.get(`/ai/conversations/${cid}`);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const newConversation = () => {
    setConversationId(null);
    setMessages([]);
    setInput("");
  };

  const deleteConversation = async (cid, e) => {
    e.stopPropagation();
    if (!window.confirm(t("ai.confirmDelete"))) return;
    try {
      await api.delete(`/ai/conversations/${cid}`);
      if (cid === conversationId) newConversation();
      loadConversations();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setSending(true);
    setInput("");
    const tempUser = { id: `temp-${Date.now()}`, role: "user", content: q };
    setMessages((m) => [...m, tempUser]);
    try {
      const { data } = await api.post("/ai/chat", { conversation_id: conversationId, message: q });
      setConversationId(data.conversation_id);
      setMessages((m) => {
        const filtered = m.map((x) => (x.id === tempUser.id ? { ...x, id: data.user_message_id } : x));
        return [
          ...filtered,
          {
            id: data.message_id,
            role: "assistant",
            content: data.answer,
            sources: data.sources,
            confidence: data.confidence,
          },
        ];
      });
      loadConversations();
    } catch (e) {
      toast.error(formatApiError(e));
      setMessages((m) => m.filter((x) => x.id !== tempUser.id));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    // When a new user message is added, scroll THAT message to the top of the
    // viewport so the leader immediately sees their question with the answer
    // starting right below it — instead of being flung to the bottom (where
    // the sources live). We only scroll once per user message so re-renders
    // (e.g. when the assistant reply arrives) don't yank the view.
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (lastUser && lastUser.id !== lastScrolledUserIdRef.current) {
      const el = document.querySelector(`[data-msg-id="${lastUser.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        lastScrolledUserIdRef.current = lastUser.id;
      }
    }
  }, [messages]);

  const hasMessages = messages.length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8 min-h-[calc(100vh-8rem)]">
      {/* Conversations sidebar */}
      <aside className="hidden lg:block lg:sticky lg:top-24 lg:self-start">
        <button
          data-testid="new-chat-btn"
          onClick={newConversation}
          className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-ink-0 font-semibold text-sm hover:bg-gold-hover mb-6"
          style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
        >
          <MessageSquarePlus className="w-4 h-4" /> {t("ai.newChat")}
        </button>
        <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-600 font-semibold mb-3 px-2">{t("ai.history")}</div>
        <div className="space-y-0.5 max-h-[70vh] overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="text-xs text-zinc-600 px-2 py-4">{t("ai.noConversations")}</div>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => openConversation(c.id)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 group ${
                  c.id === conversationId ? "bg-white/5 text-gold" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
                style={{ transitionProperty: "color, background-color", transitionDuration: "140ms" }}
              >
                <span className="flex-1 truncate">{c.title}</span>
                <span
                  onClick={(e) => deleteConversation(c.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-300 p-1"
                >
                  <Trash2 className="w-3 h-3" />
                </span>
              </button>
            ))
          )}
        </div>
      </aside>

      {/* Chat area */}
      <div className="min-w-0 flex flex-col">
        {!hasMessages ? (
          <div className="flex-1 flex items-center justify-center py-10">
            <div className="text-center max-w-2xl mx-auto animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/5 mb-8">
                <Sparkles className="w-3.5 h-3.5 text-gold" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">{t("ai.eyebrow")}</span>
              </div>
              <h1 className="font-display text-5xl font-light tracking-tight text-white mb-4">
                EM <span className="text-gold italic">AI</span>
              </h1>
              <p className="text-zinc-400 text-base leading-relaxed max-w-lg mx-auto mb-12">
                {t("ai.subtitle")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl mx-auto mb-12">
                {examplePrompts.map((p, i) => (
                  <button
                    key={i}
                    data-testid={`ai-prompt-${i}`}
                    onClick={() => setInput(p)}
                    className="text-left p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-gold/25 hover:bg-gold/[0.04] group"
                    style={{ transitionProperty: "background-color, border-color", transitionDuration: "200ms" }}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-gold mb-3" strokeWidth={1.75} />
                    <p className="text-sm text-zinc-300 group-hover:text-white leading-relaxed" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>{p}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 space-y-10 pb-40">
            {messages.map((m, idx) => {
              // Find the user question that immediately precedes this assistant message
              const prevUserMsg = m.role === "assistant"
                ? [...messages.slice(0, idx)].reverse().find((x) => x.role === "user")
                : null;
              return (
                <div key={m.id} data-msg-id={m.id} data-msg-role={m.role} className="animate-fade-in scroll-mt-24">
                  {m.role === "user"
                    ? <UserMessage msg={m} userName={user?.name} />
                    : <AssistantMessage msg={m} conversationId={conversationId} userQuestion={prevUserMsg?.content || ""} />}
                </div>
              );
            })}
            {sending && (
              <div className="flex items-start gap-4 animate-fade-in">
                <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-gold animate-shimmer" strokeWidth={1.75} />
                </div>
                <div className="flex items-center gap-2 text-sm text-zinc-500 mt-3">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="animate-shimmer">{t("ai.thinking")}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Input */}
        <div className={`${hasMessages ? "fixed bottom-0 left-0 right-0 lg:left-[260px] z-20 bg-gradient-to-t from-ink-0 via-ink-0 to-transparent pb-6 pt-10" : ""}`}>
          <div className={`${hasMessages ? "max-w-3xl mx-auto px-4 sm:px-6" : ""}`}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="em-card p-2 flex items-center gap-2"
              style={{ boxShadow: hasMessages ? "0 20px 60px -20px rgba(0,0,0,0.7)" : undefined }}
            >
              <input
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("ai.inputPlaceholder")}
                className="flex-1 bg-transparent outline-none text-white placeholder:text-zinc-500 px-4 py-3 text-base"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="ai-send"
                className="shrink-0 w-10 h-10 rounded-lg bg-gold text-ink-0 flex items-center justify-center hover:bg-gold-hover disabled:opacity-40"
                style={{ transitionProperty: "background-color, opacity", transitionDuration: "180ms" }}
                aria-label={t("ai.sendAria")}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
            <p className="text-[11px] text-zinc-600 text-center mt-3">
              {t("ai.footnote")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
